import React, { FC, useEffect, useReducer, useState } from 'react'
import {
  AccountState,
  defaultWaitlistState,
  fetchAccountState,
} from '../api/waitlist/waitlist-api'
import { useReloader } from './use-reload'
import {
  AccountStateAction,
  accountStateReducer,
} from '../components/account/account-state-reducer'

interface ContextState {
  accountState: AccountState
  loaded: boolean
  accountStateDispatcher: React.Dispatch<AccountStateAction>
  reload: () => void
}

const initialState: AccountState = {
  address: '',
  whitelisted: false,
  whitelistState: undefined, // undefined if not whitelisted
  onWaitlist: false, // false if whitelisted
  waitlistState: defaultWaitlistState,
}

export const AccountStateContext = React.createContext<ContextState>({
  accountState: initialState,
  loaded: false,
  accountStateDispatcher: () => {},
  reload: () => {},
})

export const AccountStateProvider: FC = ({ children }) => {
  const { account, nonce } = useReloader()
  const [loaded, setLoaded] = useState(false)
  const [accountState, accountStateDispatcher] = useReducer(
    accountStateReducer,
    initialState
  )

  async function reload() {
    if (account) {
      accountStateDispatcher({ type: 'connected-account', address: account })
      try {
        const newState = await fetchAccountState(account)
        accountStateDispatcher({
          type: 'fetched-account-state',
          newState,
        })
        setLoaded(true)
      } catch (e) {
        console.warn('Unable to load account state', e)
      }
    }
  }

  useEffect(() => {
    setLoaded(false)
    reload()
  }, [account, nonce])

  return (
    <AccountStateContext.Provider
      value={{
        accountState,
        loaded,
        accountStateDispatcher,
        reload,
      }}
    >
      {children}
    </AccountStateContext.Provider>
  )
}

export const useAccountState = () => React.useContext(AccountStateContext)
